import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CmCluster, CmClusterEdge, CmClusterNode } from '@bloo-team/api-client'
import { clusterLayout, type ClusterPoint } from '../../lib/case-cluster'
import { caseLabel, sevKey } from '../../lib/case-mgmt'

/**
 * A case's cluster as a graph (docs/cases.md §Case page · Clusters) — inline
 * SVG like the hunt pivot graph (GraphCanvas): viewBox pan / zoom, token
 * colors, no chart library; the layout is the pure clusterLayout. The case
 * sits in the middle, one dashed ring per hop; a node is colored by
 * severity, labelled with its title and *case-N · status · hop n*. A link is solid
 * when the relations engine made it (auto), dashed when an analyst did
 * (manual); its weight and label follow the shared entities, which hovering
 * or focusing it lists.
 *
 * Keyboard: nodes and links are focusable; Enter or Space on a node selects
 * it — the host owns the selection (the side list and the footer follow it).
 * The host keys it by hop count, so another walk starts from the whole picture.
 */

const SIZE = 1000
const R_NODE = 13
const R_START = 20

const clip = (v: string, n = 30) => (v.length > n ? `${v.slice(0, n - 1)}…` : v)

type View = { x: number; y: number; w: number; h: number }
const FULL: View = { x: 0, y: 0, w: SIZE, h: SIZE }

function zoomView(v: View, factor: number, minW: number, maxW: number): View {
  const w = Math.min(maxW, Math.max(minW, v.w * factor))
  return { x: v.x + (v.w - w) * 0.5, y: v.y + (v.w - w) * 0.5, w, h: w }
}

/** Where along a link (from its inner end) its label sits. */
const LABEL_AT = 0.62

/** Line weight (screen px) from the shared entities: 1.5 for one or none, up to 6. */
const weight = (shared: number) => Math.min(6, 1.5 + Math.max(0, shared - 1) * 1.1)

export const LINK_COLOR = { auto: 'var(--viz-1)', manual: 'var(--viz-4)' } as const

export function ClusterGraph({
  cluster,
  startId,
  selected,
  onSelect,
  statusText,
}: {
  cluster: CmCluster
  startId: string
  selected: string | null
  onSelect: (id: string) => void
  /** A status key as people read it (the catalogue's label). */
  statusText: (status: string) => string
}) {
  const points = useMemo(() => clusterLayout(cluster, SIZE), [cluster])
  const [view, setView] = useState(FULL)
  const [hot, setHot] = useState<CmClusterEdge | null>(null)
  const drag = useRef<{ px: number; py: number; vx: number; vy: number } | null>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const titles = useMemo(() => new Map(cluster.nodes.map((n) => [n.id, n.title])), [cluster])
  const hopOf = useMemo(() => new Map(cluster.nodes.map((n) => [n.id, n.distance])), [cluster])

  // Pan: one view change per animation frame, whatever the event rate.
  const pending = useRef<((v: View) => View)[]>([])
  const frame = useRef(0)
  const queueView = useCallback((f: (v: View) => View) => {
    if (typeof requestAnimationFrame !== 'function') return setView(f)
    pending.current.push(f)
    if (frame.current) return
    frame.current = requestAnimationFrame(() => {
      frame.current = 0
      const fs = pending.current
      pending.current = []
      setView((v) => fs.reduce((acc, fn) => fn(acc), v))
    })
  }, [])
  useEffect(
    () => () => {
      if (frame.current) cancelAnimationFrame(frame.current)
    },
    [],
  )
  const scale = () => {
    const r = svgRef.current?.getBoundingClientRect()
    return r && r.width > 0 ? view.w / r.width : 1
  }
  const zoom = (factor: number) => setView((v) => zoomView(v, factor, SIZE / 6, SIZE * 3))
  const strokeK = view.w / SIZE
  const maxHop = Math.max(0, ...cluster.nodes.map((n) => n.distance))
  const ringGap = maxHop ? (SIZE / 2 - 130) / maxHop : 0

  // Where the hovered link's tip sits, as a share of the visible frame.
  const tip = (() => {
    if (!hot) return null
    const a = points.get(hot.a)
    const b = points.get(hot.b)
    if (!a || !b) return null
    const left = (((a.x + b.x) / 2 - view.x) / view.w) * 100
    const top = (((a.y + b.y) / 2 - view.y) / view.h) * 100
    return left < 0 || left > 100 || top < 0 || top > 100 ? null : { left, top }
  })()

  return (
    <div className="cs-cl-graph">
      <div className="cs-cl-canvas">
        <svg
          ref={svgRef}
          viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
          role="application"
          aria-label="Case cluster — drag to pan; cases and links are focusable, Enter selects a case"
          onMouseDown={(e) => {
            if ((e.target as Element).closest('[data-node],[data-link]')) return
            drag.current = { px: e.clientX, py: e.clientY, vx: view.x, vy: view.y }
          }}
          onMouseMove={(e) => {
            const d = drag.current
            if (!d) return
            const k = scale()
            const x = d.vx - (e.clientX - d.px) * k
            const y = d.vy - (e.clientY - d.py) * k
            queueView((v) => ({ ...v, x, y }))
          }}
          onMouseUp={() => (drag.current = null)}
          onMouseLeave={() => {
            drag.current = null
          }}
        >
          {Array.from({ length: maxHop }, (_, i) => (
            <circle key={i} cx={SIZE / 2} cy={SIZE / 2} r={(i + 1) * ringGap} className="cs-cl-ring" strokeWidth={strokeK} strokeDasharray={`${4 * strokeK} ${5 * strokeK}`} />
          ))}
          <Links edges={cluster.edges} points={points} titles={titles} hopOf={hopOf} strokeK={strokeK} hot={hot?.id ?? null} onHot={setHot} />
          <Nodes nodes={cluster.nodes} points={points} maxHop={maxHop} startId={startId} selected={selected} onSelect={onSelect} statusText={statusText} />
        </svg>
        {hot && tip ? (
          <div className="cs-cl-tip" role="tooltip" style={{ left: `${tip.left}%`, top: `${tip.top}%` }}>
            <span className="hud-eyebrow">
              {hot.origin} link · {hot.shared} shared
            </span>
            {hot.entities.length ? (
              <ul>
                {hot.entities.map((e) => (
                  <li key={`${e.type}:${e.name}`}>
                    <span className="muted">{e.type}</span> <span className="mono">{e.name}</span> <span className="muted">×{e.count}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <span className="muted">No shared entities recorded.</span>
            )}
          </div>
        ) : null}
      </div>
      <span className="hud-row cs-cl-zoom">
        <button type="button" className="list-chip" aria-label="Zoom in" onClick={() => zoom(0.8)}>
          +
        </button>
        <button type="button" className="list-chip" aria-label="Zoom out" onClick={() => zoom(1.25)}>
          −
        </button>
        <button
          type="button"
          className="list-chip"
          aria-label="Reset view"
          onClick={() => {
            pending.current = []
            setView(FULL)
          }}
        >
          fit
        </button>
      </span>
    </div>
  )
}

const entityText = (e: CmClusterEdge) => e.entities.map((x) => `${x.type} ${x.name} ×${x.count}`).join(', ')

/** Links, under the nodes (memoized: a pan re-renders none). */
const Links = memo(function Links({
  edges,
  points,
  titles,
  hopOf,
  strokeK,
  hot,
  onHot,
}: {
  edges: CmClusterEdge[]
  points: ReadonlyMap<string, ClusterPoint>
  titles: ReadonlyMap<string, string>
  hopOf: ReadonlyMap<string, number>
  strokeK: number
  hot: string | null
  onHot: (e: CmClusterEdge | null) => void
}) {
  return (
    <>
      {edges.map((e) => {
        const a = points.get(e.a)
        const b = points.get(e.b)
        if (!a || !b) return null
        const w = weight(e.shared) * strokeK
        // The label sits past the middle, toward the outer case — clear of the inner case's title.
        const [near, far] = (hopOf.get(e.a) ?? 0) <= (hopOf.get(e.b) ?? 0) ? [a, b] : [b, a]
        const lx = near.x + (far.x - near.x) * LABEL_AT
        const ly = near.y + (far.y - near.y) * LABEL_AT
        const label = `${clip(titles.get(e.a) ?? e.a, 40)} and ${clip(titles.get(e.b) ?? e.b, 40)}: ${e.origin} link, ${e.shared} shared${e.entities.length ? ` — ${entityText(e)}` : ''}`
        return (
          <g
            key={e.id}
            data-link={e.id}
            tabIndex={0}
            role="img"
            aria-label={label}
            className="cs-cl-link"
            data-hot={hot === e.id || undefined}
            onMouseEnter={() => onHot(e)}
            onMouseLeave={() => onHot(null)}
            onFocus={() => onHot(e)}
            onBlur={() => onHot(null)}
          >
            {/* A wide transparent line makes a thin link easy to hover. */}
            <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="transparent" strokeWidth={Math.max(w, 14 * strokeK)} />
            <line
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
              stroke={LINK_COLOR[e.origin]}
              strokeWidth={w}
              strokeDasharray={e.origin === 'manual' ? `${8 * strokeK} ${6 * strokeK}` : undefined}
              strokeLinecap="round"
            />
            <text x={lx} y={ly - 8} textAnchor="middle" className="mono cs-cl-link-label">
              {e.shared} shared
            </text>
          </g>
        )
      })}
    </>
  )
})

/** Cases (memoized: none of its props move with a pan or a zoom). */
const Nodes = memo(function Nodes({
  nodes,
  points,
  maxHop,
  startId,
  selected,
  onSelect,
  statusText,
}: {
  nodes: CmClusterNode[]
  points: ReadonlyMap<string, ClusterPoint>
  maxHop: number
  startId: string
  selected: string | null
  onSelect: (id: string) => void
  statusText: (status: string) => string
}) {
  return (
    <>
      {nodes.map((n) => {
        const p = points.get(n.id)
        if (!p) return null
        const start = n.id === startId
        const r = start ? R_START : R_NODE
        const isSel = selected === n.id
        const status = statusText(n.status)
        const tone = `var(--bui-sev-${sevKey(n.severity)})`
        // An outer case in the top half is labelled above it, clear of the link that arrives from below.
        const above = n.distance > 0 && n.distance === maxHop && p.y < SIZE / 2
        const titleY = above ? -(r + 28) : r + 22
        const subY = above ? -(r + 10) : r + 40
        return (
          <g
            key={n.id}
            data-node={n.id}
            transform={`translate(${p.x} ${p.y})`}
            role="button"
            tabIndex={0}
            aria-label={`${caseLabel(n)} ${n.title}, ${n.severity}, ${status}, ${start ? 'this case' : `hop ${n.distance}`}`}
            aria-pressed={isSel}
            className="cs-cl-node"
            onClick={() => onSelect(n.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onSelect(n.id)
              }
            }}
          >
            {start ? <circle r={r + 7} stroke={tone} className="cs-cl-halo" /> : null}
            <circle r={r} fill={tone} className="cs-cl-dot" data-selected={isSel || undefined} />
            <text y={titleY} textAnchor="middle" className="cs-cl-title">
              {clip(n.title)}
            </text>
            <text y={subY} textAnchor="middle" className="mono cs-cl-sub">
              {caseLabel(n)} · {status} · hop {n.distance}
            </text>
          </g>
        )
      })}
    </>
  )
})
